/**
 * Owner: Masa Bayu
 * File: ShellChrome.tsx
 * Date:Time: 2026-10-17 01:22
 * Description: Shell chrome — Home/Publication in-screen marketplace · else MarketplaceTopBar chrome (§373 Fasa 1).
 */

import { useEffect, useRef } from 'react';
import type { QxreadShellLayoutContract } from '../ds/shell-density';
import { MarketplaceTopBar } from '../marketplace/MarketplaceTopBar';
import { useHubSession } from '../auth/use-hub-session';
import { STUDIO_ENTRY } from '../author/author-production-identity';
import { RoutePanel } from './RoutePanel';
import { RouteAnnouncer } from './RouteAnnouncer';

export interface ShellChromeProps {
  readonly path: string;
  readonly search: string;
  readonly allowed: boolean;
  readonly chromeMode: string;
  readonly designSystemId: string;
  readonly layout: QxreadShellLayoutContract;
  readonly onNavigate: (path: string) => void;
}

export function ShellChrome({
  path,
  search,
  allowed,
  chromeMode,
  designSystemId,
  layout,
  onNavigate,
}: ShellChromeProps) {
  const mainRef = useRef<HTMLElement>(null);
  const previousPath = useRef(path);
  const session = useHubSession();
  // Ruling Owner 2026-10-17: home ialah SoT emas — semua permukaan marketplace
  // (home + detail produk) berkongsi chrome yang sama, bukan header shell klasik.
  // Ruling Owner 2026-10-18 (§373 Fasa 1): selebihnya memakai MarketplaceTopBar
  // — shell klasik kaca + ShellNav bersara.
  // §387: dashboard PRIVAT membawa chrome sendiri (sidebar + topbar) → full-bleed.
  // QXR-FIX-4: /read/ full-bleed — mod membaca buku tiada chrome marketplace
  // (header/footer shell adalah interupsi aplikasi yang Owner buang).
  // O8 + blok 1A: /author/studio dokumen — satu chrome milik aplikasi, tiada
  // topbar/footer marketplace (PWA display: standalone lalai pengguna).
  const isMarketplaceSurface =
    path === '/' ||
    path.startsWith('/publication/') ||
    path.startsWith('/read/') ||
    path === '/author' ||
    path === '/author/dashboard' ||
    path === STUDIO_ENTRY;

  useEffect(() => {
    if (previousPath.current !== path) {
      previousPath.current = path;
      mainRef.current?.focus();
    }
  }, [path]);

  if (isMarketplaceSurface) {
    return (
      <div
        className="shell shell--marketplace-home"
        data-qx-design-system={designSystemId}
        data-qx-band={layout.band}
        data-qx-density={layout.density}
        data-qx-nav={layout.navOrientation}
        data-qx-home="true"
      >
        <a className="skip-link" href="#main">
          Skip to content
        </a>
        <RouteAnnouncer path={path} allowed={allowed} />
        <main
          id="main"
          className="shell-main shell-main--marketplace-home"
          tabIndex={-1}
          ref={mainRef}
        >
          <RoutePanel
            path={path}
            search={search}
            allowed={allowed}
            chromeMode={chromeMode}
            sessionReady={session.sessionReady}
            signedIn={session.signedIn}
            signedInName={session.signedInName}
            authSession={session.authSession}
            onNavigate={onNavigate}
            onSignedIn={session.onSignedIn}
            onSignOut={session.onSignOut}
          />
        </main>
      </div>
    );
  }

  return (
    <div
      className="shell shell--marketplace shell--topnav"
      data-qx-design-system={designSystemId}
      data-qx-band={layout.band}
      data-qx-density={layout.density}
      data-qx-nav={layout.navOrientation}
      data-qx-home="true"
    >
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <RouteAnnouncer path={path} allowed={allowed} />
      <div className="marketplace-stage">
        <MarketplaceTopBar onNavigate={onNavigate} currentPath={path} signedIn={session.signedIn} />
        <main id="main" className="shell-main" tabIndex={-1} ref={mainRef}>
          <RoutePanel
            path={path}
            search={search}
            allowed={allowed}
            chromeMode={chromeMode}
            sessionReady={session.sessionReady}
            signedIn={session.signedIn}
            signedInName={session.signedInName}
            authSession={session.authSession}
            onNavigate={onNavigate}
            onSignedIn={session.onSignedIn}
            onSignOut={session.onSignOut}
          />
        </main>
        <footer className="shell-footer shell-footer--compact">
          <p>QXP publication truth · QXReader reading runtime · QXRead marketplace</p>
        </footer>
      </div>
    </div>
  );
}
